import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import { UserPlus, Phone, Mail, Home, Award, Briefcase, Hash, DoorOpen, Laptop, Edit2 } from "lucide-react";
import {
  getSocietyCommittee,
  assignCommitteeRole,
  revokeCommitteeRole,
  getSocietyResidents,
} from "../../services/societyService";
import {
  getPropertyLeadershipConfig,
  normalizePropertyType,
  PROPERTY_CATEGORIES,
} from "../../utils/propertyStructure";
import { getCurrentRoleKey } from "../../services/authService";

const PropertyCommittee = ({ societyId, propertyType }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;

  const roleKey = getCurrentRoleKey();
  const isSuperAdmin = roleKey === "super-admin";
  const isAdmin = roleKey === "admin";
  const isSocietyAdmin = roleKey === "society-admin";
  const canManageCommittee = isSuperAdmin || isAdmin || isSocietyAdmin;

  const leadershipConfig = useMemo(
    () => getPropertyLeadershipConfig(propertyType),
    [propertyType]
  );

  const [committee, setCommittee] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignData, setAssignData] = useState({
    residentId: "",
    role: leadershipConfig.roles[0]?.value || "Member",
    deskCode: "",
    cabinCode: "",
  });

  const [memberToEdit, setMemberToEdit] = useState(null);
  const [editMemberData, setEditMemberData] = useState({
    role: "",
    deskCode: "",
    cabinCode: "",
  });
  const [updatingMember, setUpdatingMember] = useState(false);

  useEffect(() => {
    if (societyId) {
      fetchCommittee();
      fetchResidents();
    }
  }, [societyId]);

  useEffect(() => {
    setAssignData((prev) => ({
      ...prev,
      role: leadershipConfig.roles[0]?.value || "Member",
    }));
  }, [leadershipConfig]);

  const fetchCommittee = async () => {
    setLoading(true);
    const result = await getSocietyCommittee(societyId);
    setCommittee(Array.isArray(result) ? result : result?.data || []);
    setLoading(false);
  };

  const fetchResidents = async () => {
    const result = await getSocietyResidents(societyId);
    const resData = Array.isArray(result) ? result : result?.data || [];
    setResidents(resData);
  };

  const handleAssignRole = async () => {
    if (!assignData.residentId) return;
    const res = await assignCommitteeRole(societyId, assignData);
    if (res || res === undefined) {
      fetchCommittee();
      setIsAssignModalOpen(false);
      setAssignData({
        residentId: "",
        role: leadershipConfig.roles[0]?.value || "Member",
        deskCode: "",
        cabinCode: "",
      });
    }
  };

  const handleRevokeRole = async (memberId) => {
    if (window.confirm("Are you sure you want to revoke this role?")) {
      const res = await revokeCommitteeRole(societyId, memberId);
      if (res || res === undefined) {
        fetchCommittee();
      }
    }
  };

  const openEditMemberModal = (member) => {
    setMemberToEdit(member);
    setEditMemberData({
      role: member.role || member.committee_role || leadershipConfig.roles[0]?.value || "Member",
      deskCode: member.desk_code || member.deskCode || "",
      cabinCode: member.cabin_code || member.cabinCode || "",
    });
  };

  const handleSaveMemberEdit = async () => {
    if (!memberToEdit) return;
    try {
      setUpdatingMember(true);
      await assignCommitteeRole(societyId, {
        residentId: memberToEdit.id || memberToEdit.user_id,
        role: editMemberData.role,
        deskCode: editMemberData.deskCode,
        cabinCode: editMemberData.cabinCode,
      });
      setMemberToEdit(null);
      fetchCommittee();
    } catch (err) {
      console.error("Update member failed:", err);
    } finally {
      setUpdatingMember(false);
    }
  };

  return (
    <div
      style={{
        padding: "24px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header & Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h3
            style={{
              color: "#0f172a",
              marginBottom: "6px",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            {leadershipConfig.title}
          </h3>
          <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
            {leadershipConfig.subtitle}
          </p>
        </div>
        <button
          onClick={() => setIsAssignModalOpen(true)}
          style={{
            padding: "10px 16px",
            background: "var(--color-dark-bg)",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "600",
            fontSize: "13.5px",
            boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
          }}
        >
          <UserPlus size={16} /> {leadershipConfig.assignButtonText}
        </button>
      </div>

      {/* Leadership / Committee Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "20px",
        }}
      >
        {committee.length > 0 ? (
          committee.map((member) => (
            <div
              key={member.id || Math.random()}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                background: "#fff",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    borderRadius: "50%",
                    background: "#e0e7ff",
                    color: "#4f46e5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    fontWeight: "700",
                    flexShrink: 0,
                  }}
                >
                  {(member.name || "U").charAt(0).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h4
                    style={{
                      margin: "0 0 4px 0",
                      color: "#0f172a",
                      fontSize: "15.5px",
                      fontWeight: "700",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {member.name || "Unknown"}
                  </h4>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      background: "#fef2f2",
                      color: "#dc2626",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "700",
                    }}
                  >
                    <Award size={13} /> {member.role || "Member"}
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderTop: "1px solid #f1f5f9",
                  paddingTop: "14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#475569",
                    fontSize: "13px",
                    flexWrap: "wrap",
                  }}
                >
                  <Home size={15} color="#94a3b8" /> {leadershipConfig.unitLabel}:{" "}
                  {member.cabin_code ? (
                    <span
                      style={{
                        background: "#f0fdfa",
                        color: "#0d9488",
                        border: "1px solid #ccfbf1",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: 700,
                        fontSize: "12px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <DoorOpen size={13} /> Cabin {member.cabin_code}
                    </span>
                  ) : member.unit ? (
                    <strong style={{ color: "#0f172a" }}>{member.unit}</strong>
                  ) : (
                    <span style={{ color: "#64748b", fontStyle: "italic" }}>
                      Open Floor Area
                    </span>
                  )}
                </div>

                {member.desk_code ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      color: "#475569",
                      fontSize: "13px",
                    }}
                  >
                    <Hash size={15} color="#94a3b8" /> Desk Code:{" "}
                    <span
                      style={{
                        background: "#eff6ff",
                        color: "var(--color-dark-bg)",
                        border: "1px solid #dbeafe",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontWeight: 700,
                        fontSize: "12px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Laptop size={13} /> {member.desk_code}
                    </span>
                  </div>
                ) : isIndustrial ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      color: "#94a3b8",
                      fontSize: "12px",
                    }}
                  >
                    <Hash size={14} color="#cbd5e1" /> Workstation:{" "}
                    <span>Auto-calculated from floor staff</span>
                  </div>
                ) : null}

                {member.department && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      color: "#475569",
                      fontSize: "13px",
                    }}
                  >
                    <Briefcase size={15} color="#94a3b8" /> Department:{" "}
                    <strong>{member.department}</strong>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#475569",
                    fontSize: "13px",
                  }}
                >
                  <Phone size={15} color="#94a3b8" /> {member.phone || "N/A"}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#475569",
                    fontSize: "13px",
                  }}
                >
                  <Mail size={15} color="#94a3b8" /> {member.email || "N/A"}
                </div>
              </div>

              {canManageCommittee && (
                <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    onClick={() => openEditMemberModal(member)}
                    style={{
                      flex: 1,
                      padding: "7px",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      color: "var(--color-dark-bg)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "12.5px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      transition: "background 150ms ease",
                    }}
                  >
                    <Edit2 size={13} /> Edit Role
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRevokeRole(member.id)}
                    style={{
                      flex: 1,
                      padding: "7px",
                      background: "#fff",
                      border: "1px solid #cbd5e1",
                      color: "#dc2626",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "12.5px",
                      transition: "background 150ms ease",
                    }}
                  >
                    Revoke Role
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div
            style={{
              gridColumn: "1 / -1",
              padding: "40px",
              textAlign: "center",
              color: "#64748b",
              border: "1px dashed #cbd5e1",
              borderRadius: "8px",
            }}
          >
            {loading
              ? "Loading assigned leaders..."
              : `No ${leadershipConfig.title.toLowerCase()} assigned yet.`}
          </div>
        )}
      </div>

      {/* Assign Role Modal */}
      {isAssignModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => setIsAssignModalOpen(false)}
          title={leadershipConfig.modalTitle}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "13.5px" }}>
              {leadershipConfig.modalDescription}
            </p>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "13.5px",
                }}
              >
                {leadershipConfig.userSelectLabel}
              </label>
              <Select
                value={assignData.residentId}
                onChange={(e) =>
                  setAssignData({ ...assignData, residentId: e.target.value })
                }
                placeholder={leadershipConfig.userPlaceholder}
                options={residents.map((r) => ({
                  value: r.id,
                  label: `${r.name} (${leadershipConfig.unitLabel}: ${r.unit || "N/A"})`,
                }))}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "13.5px",
                }}
              >
                {leadershipConfig.roleFieldLabel}
              </label>
              <Select
                value={assignData.role}
                onChange={(e) =>
                  setAssignData({ ...assignData, role: e.target.value })
                }
                options={leadershipConfig.roles.map((r) => ({
                  value: r.value,
                  label: r.label,
                }))}
              />
            </div>

            {isIndustrial && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "600",
                      color: "#0f172a",
                      fontSize: "13.5px",
                    }}
                  >
                    Cabin Code (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. CAB-301 (Leave blank if open desk / no private cabin)"
                    value={assignData.cabinCode}
                    onChange={(e) =>
                      setAssignData({ ...assignData, cabinCode: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      fontSize: "13.5px",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                  />
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                    HODs and Directors may have private cabins, or sit in open desk areas. Leave blank if no cabin.
                  </div>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "600",
                      color: "#0f172a",
                      fontSize: "13.5px",
                    }}
                  >
                    Desk / Workstation Code (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. IT-TL-01 (Leave blank for automatic floor desk counting)"
                    value={assignData.deskCode}
                    onChange={(e) =>
                      setAssignData({ ...assignData, deskCode: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      fontSize: "13.5px",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                  />
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                    Desks are automatically calculated based on desk-eligible team members on the floor. Specific codes are optional.
                  </div>
                </div>
              </>
            )}
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "12px",
              }}
            >
              <button
                onClick={() => setIsAssignModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRole}
                disabled={!assignData.residentId}
                style={{
                  padding: "8px 18px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: assignData.residentId ? "pointer" : "not-allowed",
                  opacity: assignData.residentId ? 1 : 0.5,
                  fontWeight: "600",
                }}
              >
                Save Assignment
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Role Modal */}
      {memberToEdit && (
        <Modal
          isOpen={true}
          onClose={() => setMemberToEdit(null)}
          title={`Edit Role for ${memberToEdit.name || "Leader"}`}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "13.5px",
                }}
              >
                {leadershipConfig.roleFieldLabel}
              </label>
              <Select
                value={editMemberData.role}
                onChange={(e) =>
                  setEditMemberData({ ...editMemberData, role: e.target.value })
                }
                options={leadershipConfig.roles.map((r) => ({
                  value: r.value,
                  label: r.label,
                }))}
              />
            </div>

            {isIndustrial && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "600",
                      color: "#0f172a",
                      fontSize: "13.5px",
                    }}
                  >
                    Cabin Code (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. CAB-301 (Leave blank if open desk / no private cabin)"
                    value={editMemberData.cabinCode}
                    onChange={(e) =>
                      setEditMemberData({ ...editMemberData, cabinCode: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      fontSize: "13.5px",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "600",
                      color: "#0f172a",
                      fontSize: "13.5px",
                    }}
                  >
                    Desk / Workstation Code (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. IT-TL-01"
                    value={editMemberData.deskCode}
                    onChange={(e) =>
                      setEditMemberData({ ...editMemberData, deskCode: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      fontSize: "13.5px",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                  />
                </div>
              </>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "12px",
              }}
            >
              <button
                type="button"
                onClick={() => setMemberToEdit(null)}
                disabled={updatingMember}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveMemberEdit}
                disabled={updatingMember || !editMemberData.role}
                style={{
                  padding: "8px 18px",
                  background: "var(--color-dark-bg)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: updatingMember ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {updatingMember ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PropertyCommittee;
